<template>
  <div class="playposit-image-upload full-width">
    <Tooltip
      top
      :delay-time="0"
    >
      <v-hover
        v-model="hover"
      >
        <div
          class="absolute-full-cover"
          style="z-index: 10"
          :class="isProcessing ? 'wait' : 'pointer'"
        ></div>
        <label
          slot-scope="{ hover }"
          class="image-upload-label"
          :for="hiddenFileUploadInputId"
          :class="{
            'no-image': !hasImage
          }"
        >
          <input
            :id="hiddenFileUploadInputId"
            type="file"
            name="hidden-file-upload-input"
            class="hidden-file-upload-input"
            @change="handleImageSelection"
            aria-label="Press enter to open the file browser where you can select an image to upload."
            accept="image/*"
          />
          <v-img
            class="relative pointer"
            :aspect-ratio="1.7778"
            :src="localImage"
            max-height="200px"
            contain
            :class="{
              'dark-pulse': dragOver && hasImage,
              'light-pulse': dragOver && !hasImage,
            }"
          >
            <div
              class="absolute-full-cover hover-overlay px-3 py-2"
              :class="{
                'hover-enabled': showOverlay
              }"
              @drop="handleImageSelection"
              @dragover.prevent
              @dragenter="dragOver = true"
              @dragleave="dragOver = false"
            >
              <v-layout
                v-if="!isProcessing && (hover || dragOver)"
                justify-center
                align-center
                column
                class="full-height full-width"
              >
                <v-icon
                  :size="36"
                  color="white"
                >
                  photo
                </v-icon>
                <div class="body-2 white--text text-center text-xs-center">
                  {{dragOver ? 'Drop to upload' : 'Click or drag to upload an image'}}
                </div>
              </v-layout>
              <v-layout
                v-else-if="isProcessing"
                justify-center
                align-center
                column
                class="full-height full-width"
              >
                <v-fade-transition
                  group
                  hide-on-leave
                >
                  <v-icon
                    key="success"
                    v-if="status === 'success'"
                    :size="40"
                    color="success"
                    class="relative z-1 success-white-background"
                  >
                    check_circle
                  </v-icon>
                  <v-icon
                    key="error"
                    v-if="status === 'error'"
                    :size="40"
                    color="error"
                    class="rotate-45deg z-1 relative error-white-background"
                  >
                    add_circle
                  </v-icon>
                  <v-progress-circular
                    key="processing"
                    v-else-if="status === 'processing'"
                    indeterminate
                    :size="36"
                    color="white"
                    class="absolute-center"
                  ></v-progress-circular>
                </v-fade-transition>
              </v-layout>
            </div>
            <v-layout
              v-if="!hasImage"
              justify-center
              align-center
              column
              class="full-height full-width px-3 py-2"
            >
              <v-icon
                :size="36"
                color="grey lighten-1"
              >
                photo
              </v-icon>
              <div class="body-2 grey--text text--lighten-1 text-center text-xs-center">
                {{dragOver ? 'Drop to upload' : 'Click or drag to upload an image'}}
              </div>
            </v-layout>
          </v-img>
        </label>
      </v-hover>
      <template v-slot:text>
        <div class="text-center">Images will be stretched to cover the entire background.<br/>We recommend images be at least 1920x1080.</div>
      </template>
    </Tooltip>
  </div>
</template>

<script>

// Libraries / Plugins:
import axios from 'axios';
import _ from 'lodash';
import {
  timer,
} from '@utilities'; // eslint-disable-line

export default {
  name: 'PlaypositImageUpload',
  props: {
    image: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    localImage: '',
    dragOver: false,
    hover: false,
    status: null,
  }),
  computed: {
    hasImage() {
      return !!(this.localImage && this.localImage.length);
    },
    isProcessing() {
      return !!this.status;
    },
    hiddenFileUploadInputId() {
      return `hidden-file-upload-input-${this.$generateNumber('hidden-file-upload-input')}`;
    },
    showOverlay() {
      if (!this.hasImage) return this.isProcessing;
      return (
        this.hover
        || this.dragOver
        || this.isProcessing
      );
    },
  },
  methods: {
    async handleImageSelection(e) {
      try {
        this.status = 'processing';
        this.dragOver = false;
        e.preventDefault();
        const file = _.get(e, 'target.files[0]', _.get(e, 'dataTransfer.files[0]'));
        if (file.size / (1000 ** 2) > 50) {
          throw new Error('The maximum file size for images is 50MB.');
        }
        if (
          !file.type
          || !file.type.includes('image/')
        ) {
          throw new Error('The selected file is not an image.');
        }
        const fileFormData = new FormData();
        fileFormData.set('file', file);
        const { data } = await axios.post('https://api.playposit.com/upload/image', fileFormData);
        this.localImage = data.imageUrl;
        this.$emit('success', this.localImage);
        this.status = 'success';
        await timer(1500);
        return this.localImage;
      } catch(error) {
        this.$emit('error', error);
        this.status = 'error';
        await timer(1500);
        return error;
      } finally {
        this.status = null;
      }
    },
  },
  watch: {
    status(n) {
      this.$emit('processing', !!n);
    },
    image(n) {
      this.localImage = n;
    },
  },
  created() {
    this.localImage = this.image;
  },
};

</script>

<style lang="scss">
  .playposit-image-upload {
    position: relative;

    .full-width {
      width: 100%;
    }

    .rotate-45deg {
      transform: rotate(45deg);
    }

    .relative {
      position: relative;
    }

    .pointer {
      cursor: pointer;
    }

    .wait {
      cursor: wait;
    }

    .hidden-file-upload-input {
      height: 0;
      width: 0;
      opacity: 0;
      position: absolute;
    }

    .absolute-full-cover {
      position: absolute;
      height: 100%;
      width: 100%;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
    }

    .absolute-center {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      &.rotate-45deg {
        transform: rotate(45deg), translate(-50%, -50%);
      }
    }

    .z-1 {
      z-index: 1;
    }

    .image-upload-label {
      display: block;
      width: 100%;
      margin-bottom: 0;
      border: 2px dashed;
      border-color: white;

      &.no-image {
        border-color: #E0E0E0;
      }
    }

    .hover-overlay {
      background: rgba(0,0,0,0.6);
      opacity: 0;
      transition: opacity 0.2s ease-in-out;

      * {
        pointer-events: none;
      }

      &.hover-enabled {
        opacity: 1;
      }
    }

    .success-white-background:before {
      content: "";
      display: block;
      position: absolute;
      height: 79%;
      width: 79%;
      top: 11%;
      right: 0px;
      left: 11%;
      bottom: 0;
      background: white;
      z-index: -1;
      border-radius: 50%;
    }

    .error-white-background:before {
      content: "";
      display: block;
      position: absolute;
      height: 79%;
      width: 79%;
      top: 11%;
      right: 0px;
      left: 11%;
      bottom: 0;
      background: white;
      z-index: -1;
      border-radius: 50%;
      transform: rotate(45deg);
    }

    @keyframes dark-pulse {
      0% {
        box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.6);
      }
      70% {
        box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
      }
      100% {
        box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
      }
    }

    .dark-pulse {
      animation: dark-pulse 2s infinite;
    }

    @keyframes light-pulse {
      0% {
        box-shadow: 0 0 0 0 rgba(224, 224, 244, 0.9);
      }
      70% {
        box-shadow: 0 0 0 10px rgba(224, 224, 244, 0);
      }
      100% {
        box-shadow: 0 0 0 0 rgba(224, 224, 244, 0);
      }
    }

    .light-pulse {
      animation: light-pulse 2s infinite;
    }
  }
</style>